import React, { useEffect, useState } from 'react'
import {
    EntityInstancePath,
    setAvailableOrgList,
    setStartStep,
    updateActiveEntityStep,
    updateActiveFirstLevelStep,
    updateAvailableOrgList,
    updateEntityInstancePath,
    updateEntityInstanceType,
    updateEntitySteps,
    updateHasStartStep,
    updatePath,
    updateStartStep,
} from '../../../redux/slices/entityInstanceSlice'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import { modifingItemInRelation } from '../../../services/entityServices'
import { v4 as uuidv4 } from 'uuid'
import Instance from './Instance'
import OrgAndTemplateSelection, {
    orgAndTemplateData,
} from './OrgAndTemplateSelection'
import { EntityInstanceTypes } from '../../../constants/constants'

interface AddEditEntityInstanceProps {
    handleCloseFields: (a: boolean) => void
    isEdit: boolean
    hasAddRelation?: {
        parentInstanceId: string
        relationName: string
    }
    entityData: {
        templateName: string
        templateId: string
        entityId: string
        orgId: string
    }
    instanceType: EntityInstanceTypes.IN_TRANSIT | EntityInstanceTypes.NORMAL
}

const EntityInstance: React.FC<AddEditEntityInstanceProps> = ({
    handleCloseFields,
    isEdit,
    entityData,
    hasAddRelation,
    instanceType,
}) => {
    const dispatch = useAppDispatch()
    const orgId = useAppSelector((state) => state.org.org?.id)
    const instanceOrgId = useAppSelector(
        (state) => state.entityInstance.startStep.orgId,
    )
    const [instanceId, setInstanceId] = useState<string>('')
    const [openInstance, setOpenInstance] = useState<boolean>(false)
    const [openOrgAndTemplateSelection, setOpenOrgAndTemplateSelection] =
        useState<boolean>(false)

    useEffect(() => {
        if (instanceType) {
            dispatch(updateEntityInstanceType(instanceType))
        }
    }, [instanceType, dispatch])

    useEffect(() => {
        if (entityData?.templateId && orgId) {
            dispatch(
                setAvailableOrgList({
                    orgId,
                    templateId: entityData?.templateId,
                }),
            )
            setOpenOrgAndTemplateSelection(true)
        }
    }, [entityData, entityData?.templateId, orgId, dispatch])

    const handleAddInParentRelation = async (newEntityId: string) => {
        if (!instanceId) {
            const payload = {
                parentInstanceId: hasAddRelation?.parentInstanceId,
                childInstanceId: newEntityId,
                newChildInstanceId: '',
                relationStatus: 'ACTIVE',
                relationName: hasAddRelation?.relationName,
            }
            setInstanceId(newEntityId)
            await modifingItemInRelation(instanceOrgId, payload)
        }
    }

    const handleAddProgramStep = (hasStartStep: boolean) => {
        if (isEdit) {
            const uniqueId = uuidv4()
            const path: EntityInstancePath = {
                pathId: uniqueId,
                pathName: entityData.templateName,
                activeStep: 0,
                orgId: entityData.orgId || '',
                templateId: entityData.templateId,
                templateName: entityData.templateName,
                relationId: entityData.templateId,
                relationName: entityData.templateName,
                entityId: entityData.entityId,
                entityName: '',
                parentInstanceId: '',
                parentValues: {},
            }
            dispatch(
                updateStartStep({
                    orgId: entityData.orgId,
                    instanceExistingOrgId: entityData.orgId,
                    parentTemplateId: entityData.templateId,
                    parentTemplateName: entityData.templateName,
                    templateId: entityData.templateId,
                    templateName: entityData.templateName,
                }),
            )
            dispatch(
                updateEntityInstancePath({
                    path: [path],
                    isFirst: true,
                    hasStartStep,
                }),
            )
            dispatch(updateHasStartStep(hasStartStep))

            setOpenInstance(true)
        } else {
            const params = {
                orgId: entityData.orgId,
                templateId: entityData.templateId,
                templateName: entityData.templateName || 'Entity',
                entityId: entityData.entityId,
            }
            setOpenOrgAndTemplateSelection(false)
            setOpenInstance(true)
            dispatch(updateHasStartStep(true))
            dispatch(setStartStep(params))
        }
    }

    const onSubmitTemplateSelection = (
        orgAndTemplateData: orgAndTemplateData,
    ) => {
        const uniqueId = uuidv4()
        const path: EntityInstancePath = {
            pathId: uniqueId,
            pathName: entityData.templateName,
            activeStep: 0,
            orgId: orgAndTemplateData.orgId || '',
            templateId: orgAndTemplateData.templateId,
            templateName: orgAndTemplateData.templateName,
            relationId: orgAndTemplateData.templateId,
            relationName: orgAndTemplateData.templateName,
            entityId: '',
            entityName: '',
            parentInstanceId: '',
            parentValues: {},
        }
        dispatch(
            updateStartStep({
                orgId: orgAndTemplateData.orgId,
                instanceExistingOrgId: orgAndTemplateData.orgId,
                parentTemplateId: entityData.templateId,
                parentTemplateName: entityData.templateName,
                templateId: orgAndTemplateData.templateId,
                templateName: orgAndTemplateData.templateName,
            }),
        )
        dispatch(
            updateEntityInstancePath({
                path: [path],
                isFirst: true,
                hasStartStep: false,
            }),
        )
        setOpenInstance(true)
    }
    const handleClose = (reload: boolean) => {
        dispatch(updatePath([]))
        dispatch(updateEntitySteps([]))
        dispatch(updateActiveEntityStep(0))
        dispatch(updateActiveFirstLevelStep(0))
        dispatch(updateHasStartStep(false))
        dispatch(
            updateStartStep({
                orgId: '',
                instanceExistingOrgId: '',
                parentTemplateId: '',
                parentTemplateName: '',
                templateId: '',
                templateName: '',
            }),
        )
        dispatch(updateAvailableOrgList([]))
        dispatch(updateEntityInstanceType(EntityInstanceTypes.NORMAL))
        handleCloseFields(reload)
    }
    return (
        <>
            {openInstance ? (
                <Instance
                    handleCloseFields={handleClose}
                    {...(!isEdit &&
                    hasAddRelation &&
                    hasAddRelation?.parentInstanceId &&
                    hasAddRelation?.relationName
                        ? { handleAddInParentRelation }
                        : {})}
                />
            ) : null}
            {openOrgAndTemplateSelection ? (
                <OrgAndTemplateSelection
                    onClose={() => handleClose(false)}
                    onSubmit={onSubmitTemplateSelection}
                    handleAddProgramStep={handleAddProgramStep}
                    isEdit={isEdit}
                />
            ) : null}
        </>
    )
}

export default EntityInstance
